import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { 
  createWithdrawalMethod, 
  updateWithdrawalMethod, 
  deleteWithdrawalMethod,
  setPrimaryWithdrawalMethod 
} from '../../../services/earningsService';

const WithdrawalMethods = ({ methods = [], onRefresh }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [formData, setFormData] = useState({
    methodType: 'gcash',
    accountName: '',
    accountNumber: '',
    bankName: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const methodTypeOptions = [
    { value: 'gcash', label: 'GCash', icon: 'Smartphone' },
    { value: 'paymaya', label: 'PayMaya', icon: 'Smartphone' },
    { value: 'bank_account', label: 'Bank Account', icon: 'Building' },
    { value: 'paypal', label: 'PayPal', icon: 'Globe' },
    { value: 'other', label: 'Other', icon: 'CreditCard' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'bg-success/10 text-success';
      case 'pending':
        return 'bg-warning/10 text-warning';
      case 'rejected':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getMethodIcon = (type) => {
    const option = methodTypeOptions?.find(opt => opt?.value === type);
    return option?.icon || 'CreditCard';
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (!formData?.accountName || !formData?.accountNumber) {
        setError('Please fill in all required fields');
        return;
      }

      if (formData?.methodType === 'bank_account' && !formData?.bankName) {
        setError('Bank name is required for bank accounts');
        return;
      }

      if (editingMethod) {
        await updateWithdrawalMethod(editingMethod?.id, formData);
      } else {
        await createWithdrawalMethod(formData);
      }

      setShowAddModal(false);
      setEditingMethod(null);
      setFormData({
        methodType: 'gcash',
        accountName: '',
        accountNumber: '',
        bankName: ''
      });
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error saving withdrawal method:', err);
      setError(err?.message || 'Failed to save withdrawal method');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (methodId) => {
    if (!confirm('Are you sure you want to delete this withdrawal method?')) return;

    try {
      setLoading(true);
      await deleteWithdrawalMethod(methodId);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error deleting withdrawal method:', err);
      alert(err?.message || 'Failed to delete withdrawal method');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPrimary = async (methodId) => {
    try {
      setLoading(true);
      await setPrimaryWithdrawalMethod(methodId);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error setting primary method:', err);
      alert(err?.message || 'Failed to set primary method');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (method) => {
    setEditingMethod(method);
    setFormData({
      methodType: method?.methodType,
      accountName: method?.accountName,
      accountNumber: method?.accountNumber,
      bankName: method?.bankName || ''
    });
    setShowAddModal(true);
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-6">
        <div>
          <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-1">
            Withdrawal Methods
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage your payout destinations
          </p>
        </div>
        <Button
          size="sm"
          iconName="Plus"
          onClick={() => {
            setEditingMethod(null);
            setFormData({
              methodType: 'gcash',
              accountName: '',
              accountNumber: '',
              bankName: ''
            });
            setShowAddModal(true);
          }}
        >
          Add Method
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {methods?.map((method) => (
          <div
            key={method?.id}
            className={`relative p-4 rounded-lg border-2 transition-smooth ${
              method?.isPrimary
                ? 'border-primary bg-primary/5' :'border-border bg-muted/30 hover:bg-muted/50'
            }`}
          >
            {method?.isPrimary && (
              <div className="absolute top-2 right-2">
                <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                  Primary
                </span>
              </div>
            )}

            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg bg-card flex items-center justify-center">
                <Icon name={getMethodIcon(method?.methodType)} size={24} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground capitalize">
                  {method?.methodType?.replace('_', ' ')}
                </h3>
                <p className="text-sm text-muted-foreground truncate">{method?.accountName}</p>
              </div>
            </div>

            <div className="space-y-2 mb-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Account Number:</span>
                <span className="font-mono text-foreground">{method?.accountNumber}</span>
              </div>
              {method?.bankName && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Bank:</span>
                  <span className="text-foreground">{method?.bankName}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status:</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(method?.status)}`}>
                  {method?.status}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              {!method?.isPrimary && method?.status === 'verified' && (
                <Button
                  size="sm"
                  variant="outline"
                  fullWidth
                  onClick={() => handleSetPrimary(method?.id)}
                  disabled={loading}
                >
                  Set as Primary
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                iconName="Edit"
                onClick={() => handleEdit(method)}
                disabled={loading}
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="destructive"
                iconName="Trash2"
                onClick={() => handleDelete(method?.id)}
                disabled={loading || method?.isPrimary}
              >
                Delete
              </Button>
            </div>
          </div>
        ))}

        {methods?.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Icon name="CreditCard" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No withdrawal methods added yet</p>
            <Button
              variant="outline"
              onClick={() => setShowAddModal(true)}
            >
              Add Your First Method
            </Button>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-foreground">
                {editingMethod ? 'Edit' : 'Add'} Withdrawal Method
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingMethod(null);
                  setError('');
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-4">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              <Select
                label="Method Type"
                value={formData?.methodType}
                onChange={(e) => setFormData({ ...formData, methodType: e?.target?.value })}
              >
                {methodTypeOptions?.map((option) => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </Select>

              <Input
                label="Account Name"
                placeholder="Enter account holder name"
                value={formData?.accountName}
                onChange={(e) => setFormData({ ...formData, accountName: e?.target?.value })}
                required
              />

              <Input
                label="Account Number"
                placeholder="Enter account number"
                value={formData?.accountNumber}
                onChange={(e) => setFormData({ ...formData, accountNumber: e?.target?.value })}
                required
              />

              {formData?.methodType === 'bank_account' && (
                <Input
                  label="Bank Name"
                  placeholder="Enter bank name"
                  value={formData?.bankName}
                  onChange={(e) => setFormData({ ...formData, bankName: e?.target?.value })}
                  required
                />
              )}

              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">
                  <Icon name="Info" size={14} className="inline mr-1" />
                  Your withdrawal method will be verified within 24 hours
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  fullWidth
                  onClick={handleSubmit}
                  loading={loading}
                  disabled={loading}
                >
                  {editingMethod ? 'Update' : 'Add'} Method
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingMethod(null);
                    setError('');
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalMethods;